// src/components/Sections/ServicesSection.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import SEO from "../SEO"; // <- SEO component
import "./ServicesSection.css";

import a from "../images/1.webp";
import b from "../images/2.webp";
import c from "../images/3.webp";
import d from "../images/4.webp";
import e from "../images/5.webp";
import f from "../images/logo.gif";

const servicesData = [
  {
    id: 1,
    title: "3D Production (Modeling, Texturing & Lighting)",
    desc: "From concept to creation realistic 3D modeling, material texturing, and cinematic lighting for characters, products, and environments.",
    img: a,
  },
  {
    id: 2,
    title: "Rigging & Animation",
    desc: "Rigging characters and assets for smooth control and movement. Bringing life to your designs with expressive, precise animation.",
    img: b,
  },
  {
    id: 3,
    title: "Rendering & Compositing",
    desc: "High quality rendering and post processing to achieve photorealistic visuals for films, advertisements, and branding.",
    img: c,
  },
  {
    id: 4,
    title: "Design & Branding (Poster, Banner, UI/UX)",
    desc: "Crafting powerful visuals posters, banners, and user interface designs that enhance your brand and engage your audience.",
    img: d,
  },
  {
    id: 5,
    title: "CGI & AI Visual Design",
    desc: "Combining CGI artistry and AI driven tools to produce futuristic designs, smart visuals, and creative ad content.",
    img: e,
  },
];

export default function ServicesSection() {
  const services = useMemo(() => servicesData, []);
  const [active, setActive] = useState(null);
  const controls = useAnimation();
  const rafRef = useRef(null);
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: false });

  // --- Smooth in-view animation ---
  useEffect(() => {
    if (inView) controls.start({ opacity: 1, y: 0, transition: { duration: 0.6 } });
    else controls.start({ opacity: 0, y: 60, transition: { duration: 0.5 } });
  }, [inView, controls]);

  const handleHover = (id) => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setActive(id));
  };

  // --- Selected service for SEO ---
  const selectedService = services.find((s) => s.id === active) || null;

  return (
    <>
      {/* Dynamic SEO */}
      {selectedService && (
        <SEO
          title={`${selectedService.title} | Ankielabs`}
          description={selectedService.desc.slice(0, 160)}
          image={selectedService.img}
          url={window.location.href}
        />
      )}

      <motion.section
        id="services"
        className="services"
        ref={ref}
        initial={{ opacity: 0, y: 80 }}
        animate={controls}
      >
        {/* LEFT SIDE */}
        <div className="services-left">
          <h2>WHAT I CAN DO FOR YOU</h2>
          <p>
            As a digital artist and designer, I merge creativity and technology to
            deliver professional 3D visuals, motion, and design solutions that
            tell your brand’s story.
          </p>

          <div className="accordion">
            {services.map((service) => (
              <div
                key={service.id}
                className={`accordion-item ${active === service.id ? "active" : ""}`}
                onMouseEnter={() => handleHover(service.id)}
                onMouseLeave={() => handleHover(null)}
              >
                <h3>
                  {service.id}. <span>{service.title}</span>
                </h3>

                <AnimatePresence>
                  {active === service.id && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      {service.desc}
                    </motion.p>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {active === service.id && (
                    <motion.div
                      key={service.id}
                      className="floating-hint"
                      initial={{ opacity: 0, x: 40, rotate: 4 }}
                      animate={{ opacity: 1, x: 0, rotate: 0 }}
                      exit={{ opacity: 0, x: 40, rotate: -4 }}
                      transition={{ duration: 0.35 }}
                      style={{ willChange: "transform" }}
                    >
                      <img
                        src={service.img}
                        alt={service.title}
                        loading="lazy"
                        className="floating-hint-img"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT SIDE IMAGE */}
        <motion.div
          className="services-right"
          animate={{ y: [0, -8, 0, 8, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          style={{ willChange: "transform" }}
        >
          <div className="image-container">
            <img
              src={f}
              alt="Main service visual"
              loading="lazy"
              className="main-service-image"
              draggable="false"
            />
          </div>
        </motion.div>
      </motion.section>
    </>
  );
}
